import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Row, Col, Button, Badge, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import WorkExecutionBatchPdfGenerator from "./WorkExecutionBatchPdfGenerator";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Timeline,
  Upload,
  Image,
  Popover,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  FilePdfOutlined,
  ScheduleOutlined,
  DownOutlined,
  RightOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Button as AntButton } from "antd";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import workExecutionApis from "../../api/WorkOrderManagement/WorkExecutionApis";
import { useSelector } from "react-redux";
import SignatureModal from "../components/SignatureModal";
import ImageUploadCropper from "../components/ImageUploadCropper";

const { Panel } = AntCollapse;
const { TextArea } = Input;

interface StageLine {
  workOrderLineStageId: number;
  stageTitle: string;
  splitPercentage: number;
  rate: number;
  amount: number;
  basicAmount: number;
}

interface WorkLine {
  id: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  approvedQuantity: number;
  executedQuantity: number;
  cumulativeExecuted: number;
  percentCompletion: number;
  remainingQuantity: number;
  remarks: string;
  uom: string;
  stageLines?: StageLine[];
}

const WorkExecutionBatchDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
  const [batch, setBatch] = useState<any>(null);
  const [workOrder, setWorkOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "return" | null>(
    null
  );
  const [form] = Form.useForm();
  const [workLines, setWorkLines] = useState<WorkLine[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [currentUserId, setCurrentUserId] = useState<any>(null);
  const [approverIds, setApproverIds] = useState<any>([]);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const [signature, setSignature] = useState({
    isActive: false,
    label: "Authorised Signatory",
    image: null as string | null,
  });

  const [showSignatureModel, setShowSignatureModel] = useState(false);
  const [isModalApproveVisible, setIsModalApproveVisible] = useState(false);
  const [approveRecord, setApproveRecord] = useState<any>(null);
  const [activeSignatureContext, setActiveSignatureContext] = useState<
    "APPROVE" | "REJECT" | null
  >(null);
  const [isStrictMode, setIsStrictMode] = useState<boolean>(false);
  const [isModalRejectedVisible, setIsModalRejectedVisible] = useState(false);
  const [showActionModelVisible, setShowActionModelVisible] = useState(false);
  const [rejectRecord, setRejectRecord] = useState<any>(null);


  useEffect(() => {
    console.log("batchId", id);
    if (id) {
      loadBatchData(id);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  useEffect(() => {
    if (currentUser) {
      const { id } = currentUser;
      setCurrentUserId(id);
    }
  }, [currentUser]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const mapApiBatchDataToComponent = (apiData: any) => {
    return {
      batchId: apiData.workExeBatchId,
      batchNumber: apiData.workBatchSequenceId,
      woNumber: apiData.workOrderNumber,
      woId: apiData.workOrderId,
      workOrderTitle: apiData.workOrderTitle,
      siteName: apiData.siteName,
      orgName: apiData.orgName,
      projectName: apiData.projectName,
      status: apiData.status,
      submittedOn: apiData.submittedAt,
      submittedBy: apiData.createdByName,
      totalExecutedQuantity: apiData.totalExecutedQuantity,
      totalLines: apiData.batchLines?.length || 0,
      slaAge: Math.ceil(
        (new Date().getTime() - new Date(apiData.submittedAt).getTime()) /
        (1000 * 3600 * 24)
      ),
      batchLines: apiData.batchLines || [],
      dms: apiData.dms || [],
      attachments: apiData.attachments || [],
      paginationId: apiData.paginationId,
      approvals: apiData.executionBatchApprovals || [],
    };
  };

  const mapBatchLinesToWorkLines = (batchLines: any[]): WorkLine[] => {
    if (!batchLines || batchLines.length === 0) {
      return [];
    }

    return batchLines.map((line, index) => {
      const approvedQty = line.approvedQuantity || 0;
      const executedQty = line.executedQuantity || 0;
      const cumulativeQty =
        line.cumulativeExecutedQuantity + line.executedQuantity || 0;

      // Calculate percentage completion
      const percentCompletion =
        approvedQty > 0
          ? Math.round(
            ((cumulativeQty + executedQty) / approvedQty) * 100 * 100
          ) / 100
          : 0;

      // Calculate remaining quantity
      const remainingQuantity = Math.max(
        0,
        approvedQty - cumulativeQty - executedQty
      );

      // Map stageLines if available
      const stageLines =
        line.stageLines?.map((stage: any) => ({
          workOrderLineStageId: stage.workOrderLineStageId,
          stageTitle: stage.stageTitle || "",
          splitPercentage: stage.splitPercentage || 0,
          rate: stage.rate || 0,
          amount: stage.allocatedQuantity || 0,
          prevExecutedSubTotalAmount: stage.prevExecutedQuantity || 0,
          prevPendingApprovalExecutedSubTotalAmount:
            stage.prevPendingExecutedQuantity || 0,
          executedQuantity: stage.executedQuantity || 0,
          basicAmount: stage.basicAmount || 0,
        })) || [];

      return {
        id: line.executionBatchLineId?.toString() || index.toString(),
        workLineNumber: `WOL-${String(line.workOrderLineId).padStart(3, "0")}`,
        workLineTitle: line.taskTitle || `Task ${index + 1}`,
        description: line.taskTitle || "No description available",
        approvedQuantity: approvedQty,
        alreadyExecutedQty: line.cumulativeExecutedQuantity,
        executedQuantity: executedQty,
        cumulativeExecuted: cumulativeQty,
        percentCompletion: percentCompletion,
        remainingQuantity: remainingQuantity,
        remarks: line.contractorRemarks,
        uom: line.uomName,
        stageLines: stageLines,
      };
    });
  };

  const loadBatchData = async (batchId: string) => {
    try {
      setLoading(true);
      if (!batchId) {
        message.error("Batch ID is required");
        setLoading(false);
        return;
      }

      const response: any = await workExecutionApis.workExecutionById(batchId);

      const isStrict =
        response?.data?.isStrictApproval === true || response?.data?.isStrictApproval === 1;
      setIsStrictMode(isStrict);

      if (response.data?.executionBatchApprovals) {
        const processedApprovals = response.data.executionBatchApprovals.map((approval: any) => ({
          ...approval,
          level: approval.level?.toLowerCase() || approval.level,
          status: approval.status?.toLowerCase() || approval.status
        }));
        response.data.executionBatchApprovals = processedApprovals;

        // Filter pending approvals
        let ids = processedApprovals
          ?.filter((item: any) => item?.status?.toLowerCase() !== "approved")
          ?.map((item: any) => item?.userId);
        setApproverIds(ids);
      }

      if (response && response.status && response.data) {
        const mappedBatch = mapApiBatchDataToComponent(response.data);
        setBatch(mappedBatch);

        // Map batch lines to work lines
        const workLines = mapBatchLinesToWorkLines(response.data.batchLines);
        setWorkLines(workLines);

        // Set work order data from batch response
        setWorkOrder({
          id: mappedBatch.woId,
          woNumber: mappedBatch.woNumber,
          title: mappedBatch.workOrderTitle,
          site: mappedBatch.siteName,
          project: mappedBatch.projectName,
          organisation: mappedBatch.orgName,
        });
      } else {
        throw new Error(
          response?.message || "Failed to fetch execution batch details"
        );
      }
    } catch (error) {
      console.error("Error loading batch:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load execution batch data",
      });
    } finally {
      setLoading(false);
    }
  };

  const parseLevelNum = (level: any) => {
    if (!level && level !== 0) return NaN;
    const str = String(level).toLowerCase();

    // Handle various level formats: level1, Level2, L1, L2, etc.
    if (str.includes('level')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    } else if (str.startsWith('l')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    }
    return NaN;
  };

  const hasUserAlreadyApproved = batch?.approvals?.some(
    (a: any) =>
      a.userId === currentUser?.id &&
      a.status?.toUpperCase() === "APPROVED"
  );

  const canUserApproveReject = useCallback(() => {
    // 1. Admin can Approve/Reject anytime - Buttons are always ENABLED
    if (currentUser?.genericRole?.toLowerCase() === "admin") {
      return true;
    }

    const approvals = batch?.approvals || [];

    // Find current user's approval entry
    const userApproval = approvals.find(
      (approval: any) => approval.userId === currentUserId
    );

    if (!userApproval || userApproval.status?.toLowerCase() !== "pending") {
      return false;
    }

    const userLevelNum = parseLevelNum(userApproval.level ?? currentUser?.approvalLevel);

    // 2. Strict = 0 (Flexible mode)
    // Anyone in the approval list can approve at any time
    if (isStrictMode === false) {
      // In flexible mode, just check if user is in the approval list and pending
      return true;
    }

    // 3. Strict = 1 (Sequential mode)
    // Must follow the order. Level 2 cannot see buttons until Level 1 is "Approved"
    if (isStrictMode === true) {
      // Get all pending approvals sorted by level
      const pendingApprovals = approvals
        .filter((approval: any) => approval.status?.toLowerCase() === "pending")
        .sort((a: any, b: any) => {
          const aNum = parseLevelNum(a.level);
          const bNum = parseLevelNum(b.level);
          if (isNaN(aNum) || isNaN(bNum)) return 0;
          return aNum - bNum;
        });

      // If there are no pending approvals, user can't approve
      if (pendingApprovals.length === 0) return false;

      // Get the first pending approval (lowest level)
      const firstPending = pendingApprovals[0];
      const firstPendingLevelNum = parseLevelNum(firstPending.level);

      // Check if user is the first pending approver
      return userApproval.userId === firstPending.userId &&
        userLevelNum === firstPendingLevelNum;
    }

    return false;
  }, [currentUser, currentUserId, batch?.approvals, isStrictMode]);

  const getPendingStatusMessage = () => {
    const approvals = batch?.approvals || [];
    const pendingApprovals = approvals
      .filter((approval: any) => approval.status?.toLowerCase() === "pending")
      .sort((a: any, b: any) => {
        const aNum = parseLevelNum(a.level);
        const bNum = parseLevelNum(b.level);
        if (isNaN(aNum) || isNaN(bNum)) return 0;
        return aNum - bNum;
      });

    if (pendingApprovals?.length > 0) {
      if (!isStrictMode) {
        // Flexible mode - any approver can approve
        const approverNames = pendingApprovals.map((approval: any) => approval.userName).join(", ");
        return `Status is pending. Any of these approvers can approve: ${approverNames}`;
      } else {
        // Strict mode - must follow sequence
        const nextApprover = pendingApprovals[0];
        const lvl = parseLevelNum(nextApprover.level);
        const levelText = !isNaN(lvl) ? `Level ${lvl}` : nextApprover.roleName || "";
        return `Status is pending. Waiting for ${nextApprover.userName} (${levelText}) to approve first.`;
      }
    }

    return "Status is pending by other approvers";
  };

  const handleApproval = async (id: string, files: File[] = []) => {
    try {
      // Convert string ID to number for the API call
      const workExeBatchId = parseInt(id);

      // Call the correct API function with the correct signature
      const response: any = await workExecutionApis.approveWorkExecution(
        workExeBatchId,
        files,
      );

      if (response && response.status) {
        message.success("Execution batch approved successfully!");
        setTimeout(() => {
          navigate(
            `${process.env.PUBLIC_URL}/work-execution-pending-approvals${flowTypeQuery}`
          );
        }, 1500);
      } else {
        throw new Error(
          response?.message || "Failed to approve execution batch"
        );
      }
    } catch (error) {
      console.error("Error approving batch:", error);
      message.error("Failed to approve execution batch. Please try again.");
    }
    finally {
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
    }
  };

  const handleActionSubmit = async (values: any) => {
    try {
      console.log("Submitting action:", actionType, values);

      if (actionType === "return") {
        // Call reject API with proper request format
        const response: any = await workExecutionApis.rejectWorkExecution(
          batch.batchId,
          values.reason
        );

        if (response && response.status) {
          message.success("Execution batch rejected successfully!");
          setActionModalVisible(false);
          form.resetFields();

          setTimeout(() => {
            navigate(
              `${process.env.PUBLIC_URL}/work-execution-pending-approvals${flowTypeQuery}`
            );
          }, 1500);
        } else {
          throw new Error(
            response?.message || "Failed to reject execution batch"
          );
        }
      }
    } catch (error) {
      console.error("Error submitting action:", error);
      message.error("Failed to reject execution batch. Please try again.");
    }
  };

  const getStatusColor = (status: string): string => {
    console.log("Status:", status);
    const statusColors: { [key: string]: string } = {
      Submitted: "info",
      Pending: "warning",
      Checked: "warning",
      Verified: "success",
      Approved: "success",
      Rejected: "danger",
      Returned: "danger",
    };
    return statusColors[status] || "secondary";
  };

  // Execution Lines Table Columns for Review
  const executionLinesColumns: TableColumnsType<WorkLine> = useMemo(() => {
    const isApproved = batch?.status === "Approved";

    const baseColumns: TableColumnsType<WorkLine> = [
      {
        title: "Work Line #",
        dataIndex: "workLineNumber",
        key: "workLineNumber",
        width: 120,
        fixed: "left",
        render: (text) => <strong>{text}</strong>,
      },
      {
        title: "Work Line Title",
        dataIndex: "workLineTitle",
        key: "workLineTitle",
        width: 200,
        render: (text, record) => (
          <div>
            <div className="fw-bold">{text}</div>
            <div className="text-muted small">{record.description}</div>
          </div>
        ),
      },
      {
        title: "Approved Qty",
        dataIndex: "approvedQuantity",
        key: "approvedQuantity",
        width: 120,
        align: "right",
        render: (qty, record) => (
          <div>
            <div className="fw-bold">{qty?.toLocaleString()}</div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
      {
        title: "Executed Qty (Till Date)",
        dataIndex: "alreadyExecutedQty",
        key: "alreadyExecutedQty",
        width: 150,
        align: "right",
        render: (qty, record) => (
          <div>
            <div className="fw-bold">{qty?.toLocaleString()}</div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
      {
        title: isApproved
          ? "Executed Qty (Approved)"
          : "Executed Qty (Submitted)",
        dataIndex: "executedQuantity",
        key: "executedQuantity",
        width: 150,
        align: "right",
        render: (qty, record) => (
          <div>
            <div className="fw-bold text-primary">{qty?.toLocaleString()}</div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
    ];

    // Only add "Cumulative (After Approval)" column when status is not "Approved"
    if (!isApproved) {
      baseColumns.push({
        title: "Cumulative (After Approval)",
        key: "cumulativeAfter",
        width: 150,
        align: "right",
        render: (_, record) => (
          <div>
            <div className="fw-bold text-success">
              {record.cumulativeExecuted?.toLocaleString()}
            </div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      });
    }

    // Add remaining columns with conditional titles
    baseColumns.push(
      {
        title: isApproved ? "% Completion" : "% Completion (Post Approval)",
        key: "percentCompletion",
        width: 160,
        align: "center",
        render: (_, record: any) => {
          const percent = !isApproved
            ? ((record.cumulativeExecuted / record.approvedQuantity) *
              100 *
              100) /
            100
            : (record.executedQuantity / record.approvedQuantity) * 100;
          const color =
            percent === 100
              ? "#52c41a"
              : percent >= 75
                ? "#faad14"
                : percent >= 50
                  ? "#1890ff"
                  : "#ff4d4f";
          return (
            <Tag color={color} style={{ minWidth: 60, textAlign: "center" }}>
              {record.approvedQuantity === 0 ? 0 : percent}%
            </Tag>
          );
        },
      },
      {
        title: isApproved ? "Remaining" : "Remaining (Post Approval)",
        key: "remainingQuantity",
        width: 150,
        align: "right",
        render: (_, record: any) => (
          <div>
            <div className="fw-bold">
              {!isApproved
                ? Math.max(
                  0,
                  record.approvedQuantity - record.cumulativeExecuted
                ).toLocaleString()
                : Math.max(
                  0,
                  record.approvedQuantity - record.alreadyExecutedQty
                )}
            </div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        key: "remarks",
        width: 200,
        render: (text) => text || <span className="text-muted">---</span>,
      }
    );

    return baseColumns;
  }, [batch?.status]);

  if (loading) {
    return (
      <>
        <Head title="Execution Batch Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!batch) {
    return (
      <>
        <Head title="Execution Batch Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Execution batch not found" />
          </div>
        </Content>
      </>
    );
  }

  const getSlaColor = (age: number): string => {
    if (age <= 1) return "success";
    if (age <= 2) return "warning";
    return "danger";
  };

  const canApprove =
    batch?.status === "Submitted" ||
    batch?.status === "Pending" ||
    batch?.status === "Checked" ||
    batch?.status === "Verified";

  const handleDownloadFile = async (file: any) => {
    if (!file?.filePath) {
      message.error("File path not available");
      return;
    }

    const triggerAnchorDownload = (href: string, revoke?: () => void) => {
      const link = document.createElement("a");
      link.href = href;
      link.download = file.fileName || "download";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (revoke) setTimeout(revoke, 0);
    };

    try {
      // Fetch as blob so browsers honour the `download` attribute even for
      // cross-origin URLs (S3 / blob storage) that would otherwise just open
      // the file in a new tab.
      const res = await fetch(file.filePath);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerAnchorDownload(objectUrl, () => URL.revokeObjectURL(objectUrl));
    } catch (err) {
      // Fallback: same-origin / CORS-permitted plain anchor download.
      console.log("Blob download failed, falling back to direct link:", err);
      triggerAnchorDownload(file.filePath);
    }
  };

  const handleReject = async (id: string, reason: string, files: File[] = []) => {
    try {
      // Call the correct API function with proper parameters
      const response: any = await workExecutionApis.rejectWorkExecution(
        parseInt(id), // Convert to number
        reason,
        files
      );

      if (response && response.status) {
        message.success("Execution batch rejected successfully!");
        setTimeout(() => {
          navigate(
            `${process.env.PUBLIC_URL}/work-execution-pending-approvals${flowTypeQuery}`
          );
        }, 1500);
      } else {
        throw new Error(
          response?.message || "Failed to reject execution batch"
        );
      }
    } catch (error) {
      console.error("Error rejecting batch:", error);
      message.error("Failed to reject execution batch. Please try again.");
    } finally {
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setShowActionModelVisible(false);
    setIsModalRejectedVisible(false);
    form.resetFields();
  };

  const dataURLToFile = (dataUrl: string, fileName: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const openApproveConfirmSwal = () => {
    if (!signature.image) return;

    setIsModalApproveVisible(false);

    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve it!",
      cancelButtonText: "No, keep it",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const file = dataURLToFile(signature.image as string, "signature.png");
        await handleApproval(approveRecord, [file]);
      } else {
        setIsModalApproveVisible(true);
      }
    });
  };

  const openRejectConfirmSwal = async () => {
    try {
      await form.validateFields();

      if (!signature.image) {
        Swal.fire({
          icon: "info",
          title: "Signature Required",
          text: "Please add your signature before rejecting.",
        });
        return;
      }

      setIsModalRejectedVisible(false);

      Swal.fire({
        title: "Confirm Rejection?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Reject it!",
        cancelButtonText: "Cancel",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const values = form.getFieldsValue();
          await handleReject(rejectRecord, values.reason);
        } else {
          setIsModalRejectedVisible(true);
        }
      });
    } catch (e) {
      console.log("Validation failed:", e);
    }
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = batch.approvals;
    if (!approvalsData || approvalsData?.length === 0)
      return <Badge color="outline-danger">No</Badge>;
    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status?.toLowerCase() == "approved";
              const isRejected = approval.status?.toLowerCase() == "rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.approverName}
                  <div className="designation">{approval?.approverRole}</div>
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                  {approval.approvalStage && (
                    <div className="text-muted small">
                      <Tag
                        color="blue"
                        style={{ fontSize: "10px" }}
                      >
                        {approval.approvalStage}
                      </Tag>
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}>
          <span
            style={{
              color: "#2263b3",
            }}
          >
            <Badge color="outline-success">Yes</Badge>
          </span>

          {isStrictMode && (
            <Badge
              color="warning"
              style={{
                fontSize: "11px",
                cursor: "pointer",
                marginLeft: "4px"
              }}
              title="Sequential approvals required, Approvals must follow the sequence"
            >
              Strict
            </Badge>
          )}
        </div>
      </Popover>
    );
  };

  return (
    <>
      <Head title={`Execution Batch - ${batch.batchNumber}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {batch.batchNumber} | {batch.status}
                      </span>
                      {/* {batch.slaAge > 2 && (
                        <Badge color="danger" className="ml-2">
                          SLA Breach
                        </Badge>
                      )} */}
                    </BlockTitle>
                  </BlockHeadContent>

                  <div className="actions clearfix">
                    <ul
                      className="m-auto d-flex align-items-center justify-content-center p-0"
                      style={{ flexDirection: "row-reverse" }}
                    >
                      <li className="pt-0 pb-0">
                        <Button
                          className="bg-white"
                          outline
                          color="light"
                          onClick={() => {
                            navigate(-1);
                          }}
                        >
                          <Icon name="arrow-left" />
                          <span>Back</span>
                        </Button>
                      </li>

                      <li className="pt-0 pb-0 ms-2">
                        <Dropdown
                          isOpen={actionsDropdownOpen}
                          toggle={() => setActionsDropdownOpen(!actionsDropdownOpen)}
                        >
                          <DropdownToggle
                            color="primary"
                            className="btn-icon"
                            style={{ padding: "8px 12px" }}
                          >
                            <MoreOutlined style={{ fontSize: "18px" }} />
                          </DropdownToggle>
                          <DropdownMenu end>
                            {/* Export PDF Option (Always visible) */}
                            <DropdownItem
                              onClick={() => {
                                setPdfLoading(true);
                                const pdfTrigger = document.querySelector(
                                  "[data-pdf-generator-trigger]"
                                ) as HTMLButtonElement;
                                if (pdfTrigger) {
                                  pdfTrigger.click();
                                }
                              }}
                              className="text-primary"
                            >
                              <FilePdfOutlined className="me-2" />
                              Export PDF
                            </DropdownItem>
                            {canApprove && !hasUserAlreadyApproved && (
                              <>
                                {canUserApproveReject() ? (
                                  <>
                                    <DropdownItem
                                      onClick={() => {
                                        setApproveRecord(batch.batchId);
                                        setActiveSignatureContext("APPROVE");
                                        setIsModalApproveVisible(true);
                                      }}
                                      className="text-success"
                                    >
                                      <CheckOutlined className="me-2" />
                                      Approve
                                    </DropdownItem>
                                    <DropdownItem
                                      onClick={() => {
                                        setRejectRecord(batch.batchId);
                                        setActiveSignatureContext("REJECT");
                                        setIsModalRejectedVisible(true);
                                        form.resetFields();
                                      }}
                                      className="text-danger"
                                    >
                                      <CloseOutlined className="me-2" />
                                      Reject
                                    </DropdownItem>
                                  </>
                                ) : (
                                  <>
                                    <DropdownItem divider />
                                    {/* APPROVE (Disabled) */}
                                    <DropdownItem
                                      style={{
                                        cursor: "not-allowed",
                                        opacity: 0.6,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: "8px",
                                      }}
                                      className="text-success"
                                      onClick={(e) => e.preventDefault()}
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <CheckOutlined />
                                        <span>Approve</span>
                                      </div>

                                      <Popover
                                        content={
                                          <div style={{ padding: "8px", maxWidth: "300px" }}>
                                            <p style={{ margin: 0 }}>{getPendingStatusMessage()}</p>
                                          </div>
                                        }
                                        title="Approval Status"
                                        trigger="hover"
                                      >
                                        <InfoCircleOutlined
                                          style={{
                                            fontSize: "16px",
                                            cursor: "pointer",
                                            color: "#2263b3",
                                          }}
                                        />
                                      </Popover>
                                    </DropdownItem>

                                    {/* REJECT (Disabled) */}
                                    <DropdownItem
                                      style={{
                                        cursor: "not-allowed",
                                        opacity: 0.6,
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                      className="text-danger"
                                      onClick={(e) => e.preventDefault()}
                                    >
                                      <div
                                        className="d-flex align-items-center"
                                        style={{ gap: "13px" }}
                                      >
                                        <CloseOutlined />
                                        <span>Reject</span>
                                      </div>
                                    </DropdownItem>
                                  </>
                                )}
                              </>
                            )}
                          </DropdownMenu>
                        </Dropdown>
                      </li>
                    </ul>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${sm ? "active" : ""
                        }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2", "3"]}>
                  <Panel header="Batch Information" key="1">
                    <Col className="justify-start w-100 flex-wrap  p-3">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Batch #</div>
                          <div className="data-value w-70">
                            {batch.batchNumber || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">WO #</div>
                          <div className="data-value w-70">
                            {batch.woNumber || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {batch.siteName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Project</div>
                          <div className="data-value w-70">
                            {batch.projectName || "--"}
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-40">Status</div>
                          <div className="data-value w-70">
                            <Badge
                              color={`outline-${getStatusColor(batch.status)}`}
                            >
                              {batch.status}
                            </Badge>
                          </div>
                        </div>
                        {/* <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Organization</div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {batch.orgName || "Not specified"}
                            </Badge>
                          </div>
                        </div> */}
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-40">SLA Age</div>
                          <div className="data-value w-70">
                            <Badge
                              color={`outline-${getSlaColor(batch.slaAge)}`}
                            >
                              {batch.slaAge} days
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-40">Total Lines</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {batch.totalLines}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-40">Approval Required</div>
                          <div className="data-value w-70">
                            <ApprovalTimelineContent />
                          </div>
                        </div>
                      </div>
                      <div className="w-40 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Submitted On</div>
                          <div className="data-value w-70">
                            {dayjs(batch.submittedOn).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Submitted By</div>
                          <div className="data-value w-70">
                            {batch.submittedBy || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">
                            Work Order Title
                          </div>
                          <div className="data-value w-80">
                            {batch.workOrderTitle
                              ? batch.workOrderTitle.length > 40
                                ? `${batch.workOrderTitle.substring(0, 40)}...`
                                : batch.workOrderTitle
                              : "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">
                            Total Executed Qty
                          </div>
                          <div className="data-value w-70">
                            <Badge color="outline-success">
                              {batch.totalExecutedQuantity?.toLocaleString()}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Panel>

                  <Panel
                    header={`Execution Lines (${workLines.length})`}
                    key="2"
                  >
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        <Table
                          className="customTable"
                          columns={executionLinesColumns}
                          dataSource={workLines}
                          scroll={{ x: 1800 }}
                          bordered
                          size="small"
                          pagination={false}
                          rowKey="id"
                          expandable={{
                            expandedRowRender: (record: any) => {
                              if (
                                !record.stageLines ||
                                record.stageLines.length === 0
                              )
                                return null;

                              const totalPercentage = record.stageLines.reduce(
                                (sum: number, stage: any) =>
                                  sum + (stage.splitPercentage || 0),
                                0
                              );
                              const totalRateAmount = record.stageLines.reduce(
                                (sum: number, stage: any) =>
                                  sum + (stage.rate || 0),
                                0
                              );
                              const totalAmount = record.stageLines.reduce(
                                (sum: number, stage: any) =>
                                  sum + (stage.amount || 0),
                                0
                              );
                              const totalBasicAmount = record.stageLines.reduce(
                                (sum: number, stage: any) =>
                                  sum + (stage.basicAmount || 0),
                                0
                              );
                              const totalExecutedQty = record.stageLines.reduce(
                                (sum: number, stage: any) =>
                                  sum + (stage.executedQuantity || 0),
                                0
                              );

                              return (
                                <div
                                  style={{
                                    padding: "12px 16px",
                                    backgroundColor: "#fafafa",
                                  }}
                                >
                                  <div
                                    style={{
                                      marginBottom: 8,
                                      fontWeight: 600,
                                      color: "#1890ff",
                                    }}
                                  >
                                    <ScheduleOutlined
                                      style={{ marginRight: 8 }}
                                    />
                                    Stage Breakdown ({record.stageLines.length}{" "}
                                    stages)
                                  </div>
                                  <Table
                                    dataSource={record.stageLines.map(
                                      (stage: any, idx: number) => ({
                                        ...stage,
                                        key: idx,
                                      })
                                    )}
                                    columns={[
                                      {
                                        title: "Stage",
                                        dataIndex: "stageTitle",
                                        key: "stageTitle",
                                        width: 300,
                                      },
                                      {
                                        title: "Percentage",
                                        dataIndex: "splitPercentage",
                                        key: "splitPercentage",
                                        width: 120,
                                        render: (val: number) => (
                                          <Tag color="blue">{val}%</Tag>
                                        ),
                                      },
                                      {
                                        title: "Stage Rate",
                                        dataIndex: "rate",
                                        key: "rate",
                                        width: 120,
                                        render: (val: number) => (
                                          <span style={{ fontWeight: 500 }}>
                                            ₹
                                            {val?.toLocaleString("en-IN", {
                                              maximumFractionDigits: 2,
                                            })}
                                          </span>
                                        ),
                                      },
                                      {
                                        title: "Qty",
                                        dataIndex: "amount",
                                        key: "amount",
                                        width: 150,
                                      },
                                      {
                                        title: "Prev Executed Qty",
                                        dataIndex: "prevExecutedSubTotalAmount",
                                        key: "prevExecutedSubTotalAmount",
                                        width: 150,
                                      },
                                      {
                                        title: "Pending Executed Qty",
                                        dataIndex:
                                          "prevPendingApprovalExecutedSubTotalAmount",
                                        key: "prevPendingApprovalExecutedSubTotalAmount",
                                        width: 150,
                                      },
                                      {
                                        title: "Current Executed Qty",
                                        dataIndex: "executedQuantity",
                                        key: "executedQuantity",
                                        width: 180,
                                      },
                                      {
                                        title: "Current Executed Amount",
                                        dataIndex: "basicAmount",
                                        key: "basicAmount",
                                        width: 180,
                                        render: (val: number) => (
                                          <span
                                            style={{
                                              fontWeight: 500,
                                              color: "#52c41a",
                                            }}
                                          >
                                            ₹
                                            {val?.toLocaleString("en-IN", {
                                              maximumFractionDigits: 2,
                                            })}
                                          </span>
                                        ),
                                      },
                                    ]}
                                    pagination={false}
                                    size="small"
                                    bordered
                                    summary={() => (
                                      <Table.Summary fixed>
                                        <Table.Summary.Row
                                          style={{
                                            backgroundColor: "#e6f7ff",
                                          }}
                                        >
                                          <Table.Summary.Cell index={0}>
                                            <strong>Total</strong>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={1}>
                                            <Tag
                                              color={
                                                totalPercentage === 100
                                                  ? "green"
                                                  : "orange"
                                              }
                                            >
                                              {totalPercentage}%
                                            </Tag>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={2}>
                                            <strong>
                                              ₹{totalRateAmount.toLocaleString(
                                                "en-IN",
                                                { maximumFractionDigits: 2 }
                                              )}</strong>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={3}>
                                            {/* <strong>{totalAmount}</strong> */}
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell
                                            index={4}
                                          ></Table.Summary.Cell>
                                          <Table.Summary.Cell
                                            index={5}
                                          ></Table.Summary.Cell>
                                          <Table.Summary.Cell index={6}>
                                            <strong
                                              style={{ color: "#52c41a" }}
                                            >
                                              {totalExecutedQty}
                                            </strong>
                                          </Table.Summary.Cell>
                                          <Table.Summary.Cell index={7}>
                                            <strong
                                              style={{ color: "#52c41a" }}
                                            >
                                              ₹
                                              {totalBasicAmount.toLocaleString(
                                                "en-IN",
                                                { maximumFractionDigits: 2 }
                                              )}
                                            </strong>
                                          </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                      </Table.Summary>
                                    )}
                                  />
                                </div>
                              );
                            },
                            expandIcon: ({
                              expanded,
                              onExpand,
                              record,
                            }: any) => {
                              const hasStageLines =
                                record.stageLines &&
                                record.stageLines.length > 0;
                              if (!hasStageLines) return null;

                              return expanded ? (
                                <DownOutlined
                                  onClick={(e) => onExpand(record, e)}
                                  style={{
                                    color: "#722ed1",
                                    cursor: "pointer",
                                  }}
                                />
                              ) : (
                                <RightOutlined
                                  onClick={(e) => onExpand(record, e)}
                                  style={{
                                    color: "#722ed1",
                                    cursor: "pointer",
                                  }}
                                />
                              );
                            },
                            rowExpandable: (record: any) =>
                              !!(
                                record.stageLines &&
                                record.stageLines.length > 0
                              ),
                          }}
                        />
                      </div>
                    </div>
                  </Panel>

                  <Panel header="Attachments" key="3">
                    {batch.dms && batch.dms.length > 0 ? (
                      <div className="mt-3">
                        <div className="d-flex flex-wrap">
                          {batch.dms.map((attachment: any, index: number) => (
                            <div key={index} className="me-3 mb-2">
                              <div
                                className="border rounded p-2 d-flex align-items-center justify-content-between"
                                style={{ minWidth: "300px" }}
                              >
                                <div className="d-flex align-items-center">
                                  <PaperClipOutlined
                                    className="me-2"
                                    style={{ fontSize: "18px" }}
                                  />
                                  <div>
                                    <div
                                      className="fw-bold"
                                      style={{ fontSize: "14px" }}
                                    >
                                      {attachment.fileName}
                                    </div>
                                    <small className="text-muted">
                                      {attachment.moduleFileType ||
                                        "Attachment"}
                                    </small>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  color="primary"
                                  outline
                                  className="ms-3"
                                  onClick={() => handleDownloadFile(attachment)}
                                  title="Download file"
                                >
                                  <DownloadOutlined />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        {batch.notes && (
                          <div className="mt-3">
                            <h6>
                              <strong>Notes:</strong>
                            </h6>
                            <p>{batch.notes}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-3">
                        <Empty description="No attachments uploaded" />
                      </div>
                    )}
                  </Panel>

                  {/* <Panel header="Action History" key="4">
                    <Timeline>
                      <Timeline.Item color="blue">
                        <p>
                          <strong>Batch Submitted</strong>
                        </p>
                        <p className="text-muted">
                          {dayjs(batch.submittedOn).format("DD MMM YYYY HH:mm")}{" "}
                          by {batch.submittedBy}
                        </p>
                      </Timeline.Item>

                      {batch.lastActionBy && (
                        <Timeline.Item color="green">
                          <p>
                            <strong>Last Action</strong>
                          </p>
                          <p className="text-muted">
                            {dayjs(batch.lastActionDate).format(
                              "DD MMM YYYY HH:mm"
                            )}{" "}
                            by {batch.lastActionBy}
                          </p>
                        </Timeline.Item>
                      )}
                    </Timeline>
                  </Panel> */}
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      {/* Hidden PDF Generator - triggered from button */}
      <div
        style={{ position: "absolute", left: "-9999px", top: 0, zIndex: -1000 }}
      >
        <WorkExecutionBatchPdfGenerator
          batchData={batch}
          workLines={workLines}
          triggerAttribute="data-pdf-generator-trigger"
          onPdfGenerated={() => setPdfLoading(false)}
        />
      </div>

      {/* PDF Loading Modal */}
      <Modal
        open={pdfLoading}
        footer={null}
        closable={false}
        centered
        width={300}
        afterClose={() => {
          setTimeout(() => {
            document.body.removeAttribute("style");
          }, 50);
        }}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px", fontSize: "14px", color: "#666" }}>
            Generating PDF Preview...
          </div>
        </div>
      </Modal>

      {/* Rejection Modal */}
      <Modal
        title="Reject Execution Batch"
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleActionSubmit}>
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[
              {
                required: true,
                message: "Please provide reason for rejecting the batch",
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Please explain why you are rejecting this execution batch..."
            />
          </Form.Item>

          <div className="text-right">
            <Button
              className="me-2"
              onClick={() => setActionModalVisible(false)}
            >
              Cancel
            </Button>
            <Button color="danger" type="submit">
              Reject Batch
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Approve Work Execution"
        visible={isModalApproveVisible}
        onOk={() => {
          if (!signature.image) {
            Swal.fire({
              icon: "info",
              title: "Signature Required",
              text: "Please add your signature before approving.",
            });
            return;
          }
          openApproveConfirmSwal();
        }}

        onCancel={() => {
          setIsModalApproveVisible(false);
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span style={{ fontSize: 14, fontWeight: 600 }}>Signature</span>

            {signature.image && (
              <button
                type="button"
                onClick={() =>
                  setSignature({
                    isActive: true,
                    label: "Authorised Signatory",
                    image: null,
                  })
                }
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
              </button>
            )}
          </div>

          {signature.image ? (
            <>
              <div
                className="border mb-3 d-flex justify-content-center align-items-center"
                style={{
                  padding: 16,
                  borderRadius: 8,
                  background: "#fff",
                }}
              >
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ maxWidth: 220, maxHeight: 120, objectFit: "contain" }}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  size="sm"
                  color="outline-secondary"
                  onClick={() =>
                    setSignature({
                      isActive: true,
                      label: "Authorised Signatory",
                      image: null,
                    })
                  }
                >
                  Reset
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{
                width: "100%",
                minHeight: 220,
                border: "2px dashed #dee2e6",
                borderRadius: 8,
                backgroundColor: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalApproveVisible(false)}
                  onCloseCrop={() => setIsModalApproveVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline style={{ minWidth: 140 }} onClick={() => setActiveSignatureContext("APPROVE")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button
                  color="outline-primary"
                  size="sm"
                  style={{ minWidth: 140 }}
                  onClick={() => {
                    setIsModalApproveVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal >

      <Modal
        title="Reject Work Execution"
        visible={isModalRejectedVisible}
        onOk={openRejectConfirmSwal}
        onCancel={() => {
          handleCancel();
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea rows={3} placeholder="Type your reason here..." />
          </Form.Item>

          <div className="mt-4">
            <strong style={{ fontSize: "16px" }}>Signature</strong>
          </div>

          {signature.image && (
            <a
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() =>
                setSignature({ isActive: true, label: "Authorised Signatory", image: null })
              }
            >
            </a>
          )}


          {signature.image ? (
            <>
              <div className="signatueImgWrapper border mb-3 p-3 text-center">
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ width: "180px", height: "auto" }}
                />
              </div>
              <div className="d-flex justify-content-end mb-3">
                <Button
                  size="sm"
                  color="outline-secondary"
                  className="me-2"
                  onClick={() =>
                    setSignature({ isActive: true, label: "Authorised Signatory", image: null })
                  }
                >
                  Reset
                </Button>
              </div>

            </>
          ) : (
            <div
              style={{
                minHeight: "200px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "15px",
                border: "2px dashed #dee2e6",
                borderRadius: "8px",
                backgroundColor: "#f8f9fa"
              }}
            >
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalRejectedVisible(false)}
                  onCloseCrop={() => setIsModalRejectedVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline onClick={() => setActiveSignatureContext("REJECT")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button
                  color="outline-primary"
                  size="sm"
                  onClick={() => {
                    setIsModalRejectedVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </Form>
      </Modal>

      {showSignatureModel && (
        <SignatureModal
          modal={showSignatureModel}
          toggle={() => {
            setShowSignatureModel(false);
            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
          onSave={(data: string) => {
            setSignature((prev: any) => ({ ...prev, image: data }));
            setShowSignatureModel(false);

            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
        />
      )}
    </>
  );
};

export default WorkExecutionBatchDetails;
